<template>
    <v-card class="p-4 w-100 delegation">
        <div class="cts-form align-items-start">
            <div class="cts-field">
                <v-label class="label_required mb-1">{{ $t("res_Label_SelectUser") }}<span> *</span></v-label>
                <div class="">
                    <StructureUserSelector :showInternalUsers="true" :showInternalStructures="false"
                        :showExternalStructures="false" :excludeSelf="true" :isMultiple="false"
                        :selectedStructureUser="toUser" @structureUserSelectChanged="structureUserSelectChanged">
                    </StructureUserSelector>
                </div>
            </div>

            <div class="cts-field">
                <v-label class="label_required mb-1">{{ $t("res_Label_FromDate") }}<span> *</span></v-label>
                <v-menu content-class="p-0" v-model="from_date_menu" :close-on-content-click="false"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field class="shrink dtField" :label="$t('res_Label_SelectDate')" readonly
                            :value="utils.dateFormat(fromDate)" solo v-on="on" append-icon="mdi-calendar"
                            :tabindex="-1">
                            <template v-slot:append>
                                <v-icon v-bind="attrs" v-on="on">mdi-calendar</v-icon>
                            </template>
                        </v-text-field>
                    </template>
                    <v-date-picker :min="new Date().toISOString()" :allowed-dates="allowedDatesForFromDate"
                        :locale="selectedLang" v-model="fromDate" @input="from_date_menu = false"></v-date-picker>
                </v-menu>
            </div>

            <div class="cts-field">
                <v-label class="label_required mb-1">{{ $t("res_Label_ToDate") }}<span> *</span></v-label>
                <v-menu content-class="p-0" v-model="to_date_menu" :close-on-content-click="false"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field class="shrink dtField" :label="$t('res_Label_SelectDate')" readonly
                            :value="utils.dateFormat(toDate)" solo v-on="on" append-icon="mdi-calendar" :tabindex="-1">
                            <template v-slot:append>
                                <v-icon v-bind="attrs" v-on="on">mdi-calendar</v-icon>
                            </template>
                        </v-text-field>
                    </template>
                    <v-date-picker :allowed-dates="allowedDatesForToDate" :locale="selectedLang" v-model="toDate"
                        @input="to_date_menu = false"></v-date-picker>
                </v-menu>
            </div>

            <div class="cts-field">
                <v-label class="label_required mb-1">{{ $t("res_Label_Nodes") }}<span> *</span></v-label>
                <multi-select class="light-bg add-arrow" v-model="node" label="name" :placeholder="$t('res_Label_Select')" track-by="id"
                    open-direction="bottom" :options="nodes" :searchable="false" :internal-search="false"
                    :clear-on-select="false" :close-on-select="false" :options-limit="300" :max-height="600"
                    :show-no-results="false" :hide-selected="false" :multiple="true" ref="multiselect2">
                </multi-select>
            </div>
            <div class="cts-field">
                <v-label class="label_required mb-1">{{ $t("res_Label_Privacy") }}<span> *</span></v-label>
                <v-select v-model="privacy" :items="constants.privacies"
                    :item-text="selectedLang == 'en' ? 'name' : 'nameAr'" item-value="id" :label="$t('res_Label_Select')" solo
                    return-object></v-select>
            </div>

            <div class="cts-field cts-field-full d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div class="d-flex  gap-3">
                    <div v-if="constants.userActions.toolbarMenu.some(x => x.name.toLowerCase() == constants.actionsEnum.toolbarMenu.Sign.key)"
                        class="d-flex align-items-center">
                        <v-label></v-label>
                        <v-checkbox v-model="delegationObject.CanSign" :label="AllowSignatureLabel"></v-checkbox>
                    </div>
                    <!-- <div class="d-flex align-items-center" v-if="enableImpersonateSignatureInDelegation">
                        <v-label></v-label>
                        <v-checkbox v-model="delegationObject.CanImpersonateSignature"
                            :label="AllowImpersonateSignature"></v-checkbox>
                    </div> -->
                    <div class="d-flex align-items-center">
                        <v-label></v-label>
                        <v-checkbox v-model="delegationObject.AllowToSeeOldLetters"
                            :label="AllowToSeeOldLetters"></v-checkbox>
                    </div>
                </div>
                <div class="d-flex gap-3">
                    <v-btn @click="clearForm()" color="light-blue">{{ $t("res_ButtonText_Clear") }}</v-btn>
                    <v-btn dense @click="addOrUpdateDelegation" class=" primary">{{ $t(modeText) }} {{
                        $t("res_ButtonText_Delegation") }}</v-btn>
                </div>
            </div>
        </div>

        <hr>

        <v-data-table :mobile-breakpoint="0" class="position-relative white flex-1 d-flex overflow-auto" disable-sort
            :headers="headers" :items="items" :loading-text="LoadingLabel" hide-no-data
            :no-data-text="$t('res_Label_NoData')" :footer-props="{ 'items-per-page-text': ItemPerPageLabel }"
            caption="" :class="{ 'result_available': items.length != 0, 'no-data': items.length == 0 }" header-fixed
            fixed-header :hide-default-footer="true" :items-per-page="pagingData.pageSize">
            <template v-slot:item="row">
                <tr>
                    <td>{{ row.item.toUser }}</td>
                    <td>{{ utils.dateFormat(row.item.fromDate, 'dd/MM/yy') }}</td>
                    <td>{{ utils.dateFormat(row.item.toDate, 'dd/MM/yy') }}</td>
                    <td>{{ row.item.nodeIds }}</td>
                    <td>{{ row.item.structureName }}</td>
                    <td v-if="row.item.canSign">{{ $t('res_ButtonText_Yes') }}</td>
                    <td v-else>{{ $t('res_ButtonText_No') }}</td>
                    <!-- <td v-if="enableImpersonateSignatureInDelegation">
                        <span v-if="row.item.canImpersonateSignature">
                            {{ $t('res_ButtonText_Yes') }}
                        </span>
                        <span v-else>
                            {{ $t('res_ButtonText_No') }}
                        </span>
                    </td> -->
                    <td v-if="row.item.allowToSeeOldLetters">
                        {{ $t('res_ButtonText_Yes') }}
                    </td>
                    <td v-else>
                        {{ $t('res_ButtonText_No') }}
                    </td>
                    <td class="text-start">
                        <div class="onhover-display">
                            <div class="custom-icon-wrapper">
                                <v-icon @click="editDelegation(row.item.id)">custom-icon white-bg edit-icon</v-icon>
                            </div>
                            <div class="custom-icon-wrapper">
                                <v-icon @click="deleteDelegation(row.item.id)">custom-icon white-bg delete-icon</v-icon>
                            </div>
                        </div>
                    </td>
                </tr>
            </template>
            <template v-slot:no-data>
                <v-row class="p-5">
                    <img style="width: 118px" src="/assets/images/empty-inbox.svg" alt="" />
                    <p class="m-0">{{ $t("res_Msgs_AlertNoItemsHere") }}</p>
                </v-row>
            </template>
        </v-data-table>

        <div class="d-flex align-items-center mt-2">
            <Pagination :totalCount="pagingData.totalCount" :pageSize="pagingData.pageSize"
                :pageIndex="pagingData.pageIndex" @pageIndexChanged="pageIndexChanged" :showMargin="false"></Pagination>
            <v-btn v-if="items.length > 0" class="custom-button" @click="exportToExcelOrPdf(1)"
                v-tooltip="$t('res_Export_To_Excel')"><v-icon class="bg-gray black--text custom-button"
                    style="color: #388729 !important; background-image: none;">mdi-file-excel</v-icon></v-btn>
            <v-btn v-if="items.length > 0" class="custom-button" @click="exportToExcelOrPdf(2)"
                v-tooltip="$t('res_Export_To_Pdf')"><v-icon
                    class="bg-gray custom-button">mdi-file-pdf-box</v-icon></v-btn>
        </div>
    </v-card>
</template>

<script>
import axios from 'axios';
import constants from '@/assets/js/constants'
import MultiSelect from 'vue-multiselect'
import * as utils from '@/assets/js/common.js'
import Pagination from '@/components/Pagination.vue'
import StructureUserSelector from '@/components/StructureUserSelector.vue'
import $ from 'jquery'

export default {
    name: 'Delegation',
    components: { MultiSelect, Pagination, StructureUserSelector },
    data: function () {
        return {
            countOnly: false,
            isExport: false,
            exportType: 0,
            from_users: [],
            to_users: [],
            structure: { id: 0, isUser: false, name: '' },
            delegationObject: {
                FromUserId: 0,
                ToUserId: 0,
                FromDate: window.newDate().toISOString().substr(0, 10),
                ToDate: window.newDate().toISOString().substr(0, 10),
                CategoryIds: "",
                NodeIds: "",
                StructureId: 0,
                FromStructureId: 0,
                CanSign: true,
                //CanImpersonateSignature: false,
                AllowToSeeOldLetters: true,
                privacyId: 0
            },
            constants: constants,
            node: [],
            toUser: null,
            modeText: 'Add',
            delegationId: 0,
            structuresLoading: false,
            structures: [],
            loading: false,
            items: [],
            value: null,
            gridHeaders: [],
            dataLoading: false,
            categories: [{ id: 1, name: 'Incoming' }, { id: 2, name: 'Outgoing' }, { id: 3, name: 'Internal' }],
            nodes: [{ id: 2, name: this.$t('res_TabsName_Inbox') }, { id: 6, name: this.$t('res_TabsName_Sent') },
            { id: 9, name: this.$t('res_TabsName_FollowUp') }, { id: 10, name: this.$t('res_TabsName_CcedInbox') }],
            options: ['list', 'of', 'options'],
            headers: [
                { text: window.translate("res_Label_To"), value: 'toUser' },
                { text: window.translate("res_Label_FromDate"), value: 'fromDate' },
                { text: window.translate("res_Label_ToDate"), value: 'toDate' },
                { text: window.translate("res_Label_Nodes"), value: 'nodeIds' },
                { text: window.translate("res_Label_Structure"), value: 'structureName' },
                { text: window.translate("res_Label_CanSign"), value: 'canSign' },
                //{ text: window.translate("res_Label_ImpersonateSignature"), value: 'canImpersonateSignature', class: constants.enableImpersonateSignatureInDelegation ? '' : 'hidden-header' },
                { text: window.translate("res_Label_SeeOldLetters"), value: 'AllowToSeeOldLetters'},
                { text: '', value: 'actions', sortable: false, width: '90px' },
            ],
            from_date_menu: false,
            to_date_menu: false,
            AllowSignatureLabel: '',
            LoadingLabel: '',
            NoDataLabel: '',
            ItemPerPageLabel: '',
            fromDate: window.newDate().toISOString().substr(0, 10),
            toDate: window.newDate().toISOString().substr(0, 10),
            pagingData: {
                pageSize: 10,
                pageIndex: 0,
                totalCount: 0
            },
            utils: utils,
            AllowImpersonateSignature: '',
            AllowToSeeOldLetters: '',
            privacy: null,
            enableImpersonateSignatureInDelegation: constants.enableImpersonateSignatureInDelegation,
            seeOldLettersDefaultValue: true,
            selectedLang: 'en', 
        }
    },
    watch:
    {
        fromDate(val) {
            this.fromDateFormatted = utils.dateFormat(this.fromDate)
        },
        toDate(val) {
            this.toDateFormatted = utils.dateFormat(this.toDate)
        },
        // 'delegationObject.CanImpersonateSignature': function (val) {
        //     if (val) {
        //         this.delegationObject.CanSign = true
        //     }
        // },
        // 'delegationObject.CanSign': function (val) {
        //     if (!val) {
        //         this.delegationObject.CanImpersonateSignature = false
        //     }
        // }
    },
    methods: {
        allowedDatesForFromDate(val) {
            if(this.toDate == "") return true;
            return val <= this.toDate;
        },
        allowedDatesForToDate(val) {
            if(this.fromDate == "") return true;
            return val >= this.fromDate;
        },
        exportToExcelOrPdf(exportType) {
            this.isExport = true;
            this.exportType = exportType;
            this.getAllDelegations(false);
        },
        getAllDelegations: function (isResetPaging) {
            if (isResetPaging) {
                this.pagingData.totalCount = 0;
                this.pagingData.pageIndex = 0;
            }

            this.dataLoading = true;
            window.showLoading();
            if (this.isExport) {
                var extension = this.exportType == 1 ? '.xlsx' : '.pdf';
                axios({
                    url: constants.baseApiUrl + `Delegation/GetAllActiveDelegationsFromUser?pageIndex=${this.pagingData.pageIndex}&pageSize=${this.pagingData.pageSize}&isExport=${this.isExport}&exportType=${this.exportType}`,
                    method: 'POST',
                    responseType: 'blob',
                })
                    .then((res) => {
                        var blob = new Blob([res.data], { type: "application/octetstream" });
                        const link = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.setAttribute("download", "ExportedData" + `${extension}`);
                        a.setAttribute("href", link);
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                    })
                    .catch((err) => window.hideLoading())
                    .finally(() => window.hideLoading())
                this.isExport = false;
                this.exportType = 0;
            } else {
                axios.post(constants.baseApiUrl + "Delegation/GetAllActiveDelegationsFromUser?pageIndex=" + this.pagingData.pageIndex + "&pageSize=" + this.pagingData.pageSize)
                    .then((res) => {
                        this.items = res.data;
                        this.dataLoading = false;
                        this.items.forEach(element => {
                            element.fromDate = utils.GetDateOnly(element.fromDate)
                            element.toDate = utils.GetDateOnly(element.toDate)

                            var delegationNodes = ''

                            var node_array = element.nodeIds.split(',')
                            node_array.forEach((element, index) => {
                                var nodeObject = this.nodes.find((obj) => obj.id == element)
                                if (index == 0)
                                    delegationNodes += nodeObject.name
                                else
                                    delegationNodes += ', ' + nodeObject.name
                            });
                            element.nodeIds = delegationNodes;
                        });

                        // A page that came back short is the last one, so the total is already
                        // known here. Only a full page can have more rows behind it, and only
                        // then is the countOnly round trip worth making.
                        if (this.items.length < this.pagingData.pageSize) {
                            this.pagingData.totalCount = (this.pagingData.pageIndex * this.pagingData.pageSize) + this.items.length;
                            return;
                        }

                        axios.post(constants.baseApiUrl + "Delegation/GetAllActiveDelegationsFromUser?pageIndex=" + this.pagingData.pageIndex
                            + "&pageSize=" + this.pagingData.pageSize + "&countOnly=true")
                            .then((res) => {
                                this.pagingData.totalCount = parseInt(res.data[0].numberOfRows);
                            })
                            .catch((err) => console.warn(err));
                    })
                    .catch((err) => console.warn(err))
                    .finally(() => { window.hideLoading(); });
            }
        },
        deleteDelegation: function (delegationId) 
        {
            window.showInformation('res_Msgs_Confirmation', window.translate("res_Msgs_DeleteDelegationConfirmation"),
                () => {
                    axios.post(constants.baseApiUrl + 'Delegation/Delete?delegationId=' + delegationId)
                        .then((res) => 
                        {
                            this.getAllDelegations()
                            this.clearForm()
                        })
                        .catch((err) => console.warn(err))
                        .finally(() => this.loading = false)
                },
                () => { })
        },
        editDelegation: function (delegationId) {
            this.modeText = 'Update';
            this.delegationId = delegationId;
            axios.get(constants.baseApiUrl + "Delegation/GetByDelegationId?delegationId=" + delegationId)
                .then(res => {
                    if (res.data != null) {
                        this.setEditValues(res.data);
                    }
                })
                .catch(err => console.warn(err))
        },
        setEditValues: function (item) {
            var fromDate = new Date(item.fromDate);
            var toDate = new Date(item.toDate);
            this.fromDate = new Date(fromDate - (new Date()).getTimezoneOffset() * 60000).toISOString().slice(0, 10);
            this.toDate = new Date(toDate - (new Date()).getTimezoneOffset() * 60000).toISOString().slice(0, 10);
            this.delegationObject.CanSign = item.canSign;
            //this.delegationObject.CanImpersonateSignature = item.canImpersonateSignature;
            this.privacy = constants.privacies.find(x=>x.id==item.privacyId)
            this.delegationObject.AllowToSeeOldLetters = item.allowToSeeOldLetters

            // setting node
            this.node = [];
            var node_array = item.nodeIds.split(',');
            node_array.forEach(element => {
                var nodeObject = this.nodes.find((obj) => obj.id == element);
                this.node.push(nodeObject)
            });

            // setting to user
            this.toUser = { id: item.toUserId + '-' + item.structureId, name: item.toUser };
            this.delegationObject.ToUserId = item.toUserId
            this.delegationObject.StructureId = item.structureId
        },
        clearForm: function () 
        {
            let today = new Date();
            let sevenDaysLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

            this.modeText = 'Add';
            this.delegationObject.CategoryIds = '';
            this.delegationObject.NodeIds = '';
            this.delegationObject.FromUserId = 0;
            this.delegationObject.ToUserId = 0;
            this.delegationObject.StructureId = 0;
            this.delegationObject.FromStructureId = 0;
            this.delegationObject.CanSign = false;
            //this.delegationObject.CanImpersonateSignature = false;
            this.delegationObject.AllowToSeeOldLetters = true;
            this.structures = [];
            this.fromUsers = [];
            this.privacy = null;
            this.node = [];
            this.toUser = null;
            this.structure = { id: 0, isUser: false, name: '' };
            this.toDate = '';
            this.fromDate = ''

            this.delegationObject.AllowToSeeOldLetters = this.seeOldLettersDefaultValue
        },
        addOrUpdateDelegation: function () {
            var allNodes = this.node.filter(element => element != null).map(element => element.id).join(',');

            this.delegationObject.NodeIds = allNodes;

            if (this.modeText == 'Add')
                this.addDelegation()
            else
                this.updateDelegation()
        },
        addDelegation: function () {
            var user = JSON.parse(sessionStorage.getItem("user"))

            if (this.delegationObject.ToUserId == 0 || this.delegationObject.NodeIds == '' || this.delegationObject.StructureId == 0 || this.fromDate == '' || this.toDate == '') {
                window.showAlert("res_Msgs_FillRequired", "warning")
            }
            else if (!this.privacy || !this.privacy.id) {
                window.showAlert("res_Msgs_fillPrivacy", "warning")
            }
            else if (new Date(this.fromDate) > new Date(this.toDate)) {
                window.showAlert("res_Msgs_DelegationDateCompare", "warning")
            }
            else if (this.delegationObject.FromUserId == this.delegationObject.ToUserId) {
                window.showAlert('res_Msgs_SameUserCantBeDelegated', 'warning')
            }
            else {
                // this.delegationObject.FromStructureId = user.profile.StructureId
                // this.delegationObject.FromUserId = user.profile.Id;
                this.delegationObject.FromDate = this.fromDate;
                this.delegationObject.ToDate = this.toDate;
                this.delegationObject.privacyId = this.privacy.id

                axios.post(constants.baseApiUrl + 'Delegation/Insert', this.delegationObject)
                    .then(res => {
                        if (res.data == 1) {
                            window.showAlert('res_Msgs_UserDelegatedAlready', 'warning')
                        }
                        else if (res.data == 2) {
                            window.showAlert('res_Msgs_IncorrectDuration', 'warning')
                        }
                        else if (res.data == 3) {
                            window.showAlert('res_Msgs_SomeCategoryAltreadySelected', 'warning')
                        }
                        else {
                            window.showAlert('res_Msgs_DelegationAddedSuccess', 'success')
                            this.getAllDelegations(false)
                            this.clearForm()
                        }
                    })
                    .catch(err => console.warn(err))
            }
        },
        updateDelegation: function () {
            if (this.delegationObject.ToUserId == 0 || this.delegationObject.NodeIds == '') {
                window.showAlert("res_Msgs_FillRequired", "warning")
            }
            else if (!this.privacy || !this.privacy.id) {
                window.showAlert("res_Msgs_fillPrivacy", "warning")
            }
            else if (this.delegationObject.FromUserId == this.delegationObject.ToUserId) {
                window.showAlert('res_Msgs_SameUserCantBeDelegated', 'warning')
            }
            else if (new Date(this.fromDate) > new Date(this.toDate)) {
                window.showAlert("res_Msgs_DelegationDateCompare", "warning")
            }
            else {
                let myobj = this.delegationObject;
                myobj.Id = this.delegationId;
                myobj.FromDate = this.fromDate;
                myobj.ToDate = this.toDate;
                myobj.privacyId = this.privacy.id
                myobj.AllowToSeeOldLetters = this.delegationObject.AllowToSeeOldLetters

                axios.post(constants.baseApiUrl + 'Delegation/Update', myobj)
                    .then(res => {
                        if (res.data == 1) {
                            window.showAlert('res_Msgs_UserDelegatedAlready', 'warning')
                        }
                        else if (res.data == 2) {
                            window.showAlert('res_Msgs_IncorrectDuration', 'warning')
                        }
                        else if (res.data == 3) {
                            window.showAlert('res_Msgs_SomeCategoryAltreadySelected', 'warning')
                        }
                        else {
                            window.showAlert('res_Msgs_DelegationUpdation', 'success')
                            this.getAllDelegations()
                            this.clearForm()
                        }
                    })
                    .catch(err => console.warn(err))
            }
        },

        CapitalizeFirstLetter(str) {
            return utils.CapitalizeFirstLetter(str)
        },
        GetMultiSelectSubHeaderDisplay(options) {
            return utils.GetMultiSelectSubHeaderDisplay(options)
        },
        openMultiSelectCtrl(id) {
            if (id === 1) this.$refs.multiselect1.activate()
            else if (id === 2) this.$refs.multiselect2.activate()
            else if (id === 3) this.$refs.multiselect3.activate()
        },
        pageIndexChanged(pagingData) {
            this.pagingData.pageSize = pagingData.pageSize;
            this.pagingData.pageIndex = pagingData.pageIndex;

            this.getAllDelegations();
        },
        structureUserSelectChanged(selectedItem) {
            if (selectedItem) {
                this.toUser = selectedItem
                this.delegationObject.ToUserId = selectedItem.id.split('-')[0]
                this.delegationObject.StructureId = selectedItem.id.split('-')[1]
            } else {
                this.toUser = null
                this.delegationObject.ToUserId = 0
                this.delegationObject.StructureId = 0
            }
        },
        getUserDefaultSettings()
        {
            axios.get(constants.baseApiUrl + 'User/GetUserDefaultSettings')
                .then(response => 
                {
                    if (response.data != null && response.data) 
                    {
                        this.seeOldLettersDefaultValue = response.data.seeOldLettersDefaultValue
                        this.delegationObject.AllowToSeeOldLetters = this.seeOldLettersDefaultValue
                    }
                })
                .catch(err => console.warn(err))
        },
    },
    mounted: function () 
    {
        this.selectedLang = localStorage.getItem('lang')
        constants.enableImpersonateSignatureInDelegation = (constants.enableImpersonateSignatureInDelegation === 'true')
        $(".v-data-footer__pagination").insertAfter($(".v-data-footer__icons-before"));
        this.AllowSignatureLabel = window.translate("res_Label_AllowSignature")
        this.AllowImpersonateSignature = window.translate("res_Label_AllowImpersonateSignature")
        this.AllowToSeeOldLetters = window.translate("res_Label_SeeOldLetters")
        this.LoadingLabel = window.translate("res_Msgs_LoadingText")
        this.NoDataLabel = window.translate("res_Label_NoData")
        this.ItemPerPageLabel = window.translate("res_Label_ItemPerPage")
        this.getAllDelegations()
        this.getUserDefaultSettings()
    },
}
</script>

<style>


.delegation .cts-form {
    display: flex;
    flex-wrap: wrap;
}

.dtField .v-input__slot {
    height: 40px !important;
}

.hidden-header {
    display: none;
}

.custom-button {
    background: none !important;
    padding: 0 !important;
    margin: 0 !important;
}

.multiselect__tag span {
    margin-right: 15px;
}
</style>